import React from 'react';
import { CapabilityNoetivraNote } from './CapabilityNoetivraNote';
import { motion } from 'framer-motion';
import { TrendingUp, Target, CheckCircle, XCircle } from 'lucide-react';
export function ConsequentialThinkingPage() {
  return <div className="min-h-screen bg-surface-bg" data-id="element-204" data-mp="w2fo3ki06ukp">
      {/* Hero Section */}
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-28 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto" data-id="element-205" data-mp="r3w2xmb8hmte">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="text-center" data-id="element-206" data-mp="1ns2d0oqxd7f2">
          <div className="inline-flex items-center gap-2 mb-6" data-id="element-207" data-mp="19tuam1b4ddwi">
            <span className="w-8 h-px bg-teal" data-id="element-208" data-mp="19vovjfme560l"></span>
            <span className="text-xs font-semibold text-teal uppercase tracking-[0.2em]" data-id="element-209" data-mp="3ea26uf7qzja">
              Capability
            </span>
            <span className="w-8 h-px bg-teal" data-id="element-210" data-mp="3ulmmvjfb1v3y"></span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-ink-dark leading-tight mb-8" data-id="element-211" data-mp="2bmi0bcop1bjo">
            Consequential Thinking
          </h1>

          <p className="text-xl text-ink-muted leading-relaxed max-w-3xl mx-auto" data-id="element-212" data-mp="21n96qkxqpyxu">
            At SyNeural, consequential thinking refers to the ability to
            understand that actions produce downstream effects, often beyond the
            immediate moment. It is the capacity to think through tradeoffs,
            anticipate second-order effects, recognize how one decision can
            reshape later choices, and evaluate whether a short-term gain may
            create a larger long-term cost.
          </p>
        </motion.div>
      </section>

      {/* Introduction */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-213" data-mp="18ybvh1ry52el">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-214" data-mp="190eqk07yhlq6">
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-215" data-mp="q08juia9ei1l">
            This capability matters because many of the most damaging mistakes
            in life, institutions, and government do not come from pure
            ignorance. They come from narrow thinking that focuses only on the
            immediate, the visible, or the emotionally satisfying, while
            ignoring what comes next. Consequential thinking helps people move
            beyond impulse and toward more grounded, forward-looking judgment.
          </p>
        </motion.div>
      </section>

      {/* Why It Matters */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-surface-muted border-y border-line" data-id="element-216" data-mp="3ojp8m4slvpqj">
        <div className="max-w-4xl mx-auto" data-id="element-217" data-mp="3ohckf9w98j51">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-218" data-mp="3jjkqkac1umy7">
            <div className="flex items-center gap-4 mb-6" data-id="element-219" data-mp="28yodpoirk5b3">
              <div className="w-12 h-12 rounded-lg bg-navy flex items-center justify-center text-white" data-id="element-220" data-mp="2dsj2h6d87ah1">
                <Target size={24} data-id="element-221" data-mp="2ipssan4j1o5e" />
              </div>
              <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-222" data-mp="2mrw4xf2xmys9">
                Why It Matters
              </h2>
            </div>
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-223" data-mp="2rsk5ard1wrz5">
              Consequential thinking is central to responsibility. It shapes how
              people plan, how they judge risk, how they handle incentives, and
              how they make choices that affect other people, future
              opportunities, and larger systems. Weak consequential thinking
              leads to impulsive behavior, poor planning, preventable tradeoffs,
              and decisions that feel good in the moment but create instability
              over time. Strong consequential thinking improves judgment under
              uncertainty and supports better long-range outcomes.
            </p>
          </motion.div>
        </div>
      </section>

      {/* What Strong Consequential Thinking Looks Like */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-224" data-mp="2a3gb8herbefq">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-225" data-mp="254wpoi42s4nw">
          <div className="flex items-center gap-4 mb-6" data-id="element-226" data-mp="1qdmqgnaglzl0">
            <div className="w-12 h-12 rounded-lg bg-teal flex items-center justify-center text-white" data-id="element-227" data-mp="1mdm147lto3z6">
              <CheckCircle size={24} data-id="element-228" data-mp="1c9p550hjzlc1" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-229" data-mp="1c849t8nezyd4">
              What Strong Consequential Thinking Looks Like
            </h2>
          </div>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-230" data-mp="12capv47d92lb">
            People with strong consequential thinking ask what happens next,
            what side effects may follow, what risks may emerge later, and how
            today's decisions constrain tomorrow's options. They understand that
            problems often spread through systems rather than remaining
            isolated. They are more likely to think in timelines, tradeoffs, and
            chain reactions rather than immediate gratification or symbolic
            gestures.
          </p>
        </motion.div>
      </section>

      {/* What Weak Consequential Thinking Looks Like */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white border-y border-line" data-id="element-231" data-mp="elneg8y8ruu0">
        <div className="max-w-4xl mx-auto" data-id="element-232" data-mp="ejanx7ef4c5c">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-233" data-mp="2h8lqvdx8xx98">
            <div className="flex items-center gap-4 mb-6" data-id="element-234" data-mp="22hbrnj3mrs6c">
              <div className="w-12 h-12 rounded-lg bg-navy-light flex items-center justify-center text-white" data-id="element-235" data-mp="1xleajakjmhsi">
                <XCircle size={24} data-id="element-236" data-mp="1thyhspblh1l3" />
              </div>
              <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-237" data-mp="1ojet4h5m9bp1">
                What Weak Consequential Thinking Looks Like
              </h2>
            </div>
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-238" data-mp="1jmofs1l74u7b">
              Weak consequential thinking often appears as impulsivity,
              short-termism, simplistic problem solving, and failure to
              anticipate foreseeable outcomes. It may show up in personal life
              as poor planning, in institutions as reactive policy, and in
              leadership as decisions that generate unintended consequences.
              People weak in this area may focus heavily on intentions, slogans,
              or emotional relief while neglecting what their choices actually
              produce.
            </p>
          </motion.div>
        </div>
      </section>

      {/* How It Can Be Strengthened */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-239" data-mp="37i31tktt0ecd">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-240" data-mp="37k5wwj9tcxny">
          <div className="flex items-center gap-4 mb-6" data-id="element-241" data-mp="wq9t1l76kxax">
            <div className="w-12 h-12 rounded-lg bg-green-soft flex items-center justify-center text-white" data-id="element-242" data-mp="woqdxxx9gsaq">
              <TrendingUp size={24} data-id="element-243" data-mp="1hulplrjgehs6" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-244" data-mp="1cw0prt846049">
              How It Can Be Strengthened
            </h2>
          </div>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-245" data-mp="1rm89sddrrr72">
            Consequential thinking can be developed through scenario planning,
            systems thinking, long-range goal setting, case studies in
            unintended consequences, structured reflection, and decision review.
            It improves when people are trained to ask not only whether an
            action feels right, but what it will likely do over time. In the
            SyNeural framework, it is treated as a critical capability for
            adulthood, institutional reliability, and leadership readiness.
          </p>
        </motion.div>
      </section>
      <CapabilityNoetivraNote capability="Consequential thinking" data-id="element-246" data-mp="b6ovxigqq2l7" />
    </div>;
}